import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

export const useNoteForm = (mode, initialData, onSuccess, isFieldPage) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isEditMode = mode === "edit";
  const fieldId = useSelector((state) => state.field?.data?.data?.id);
  const hasDefaultField = fieldId && isFieldPage;

  const getDefaultValues = () => {
    if (!initialData) {
      return {
        field: hasDefaultField ? fieldId : "",
        noteType: "",
        title: "",
        diseaseType: "",
        noteText: "",
      };
    }

    return {
      field: initialData.field?.id || "",
      noteType: initialData.note_type || "",
      title: initialData.title || "",
      diseaseType: initialData.disease?.id || "",
      noteText: initialData.description || "",
    };
  };

  const form = useForm({
    defaultValues: getDefaultValues(),
  });

  const getInitialImages = () => {
    if (!initialData?.photo) return [];

    return initialData.photo.map((img) => ({
      id: img.id,
      preview: img.photo,
      name: img.name || `photo-${img.id}`,
      size: img.size || 0,
      type: img.type || "image/jpeg",
      isExisting: true,
      file: null,
    }));
  };

  const [images, setImages] = useState(getInitialImages());
  const [diseaseLocation, setDiseaseLocation] = useState(
    initialData?.position || [],
  );
  const { isLoading, success, error } = useSelector((state) => state.fieldNote);

  const handleFileChange = useCallback((event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    const newImages = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: null,
      name: file.name,
      size: file.size,
      type: file.type,
      isExists: false,
    }));

    setImages((prev) => [...prev, ...newImages]);
    event.target.value = "";
  }, []);

  const handleRemoveImage = useCallback((imageId) => {
    setImages((prev) => {
      const imageToRemove = prev.find((img) => img.id === imageId);
      if (imageToRemove?.preview) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return prev.filter((img) => img.id !== imageId);
    });
  }, []);

  const handleAddPhotoClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleMarkerLocationSelect = useCallback((coordinates) => {
    setDiseaseLocation(coordinates || []);
  }, []);

  const resetFormData = useCallback(() => {
    form.reset();
    setImages((prevImages) => {
      prevImages.forEach(
        (image) => image.preview && URL.revokeObjectURL(image.preview),
      );
      return [];
    });
    setDiseaseLocation(null);
  }, [form]);

  useEffect(() => {
    if (success) {
      if (isFieldPage && onSuccess) {
        resetFormData();
        onSuccess();
      } else if (isEditMode && onSuccess && isFieldPage) {
        onSuccess();
      } else {
        resetFormData();
        navigate("/fieldNotebook");
      }
    }
  }, [success, isEditMode, onSuccess, resetFormData, navigate, isFieldPage]);

  useEffect(() => {
    return () => {
      images.forEach(
        (image) => image.preview && URL.revokeObjectURL(image.preview),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    form,
    fileInputRef,
    images,
    diseaseLocation,
    isLoading,
    error,
    isEditMode,
    setDiseaseLocation,
    handleFileChange,
    handleRemoveImage,
    handleAddPhotoClick,
    handleMarkerLocationSelect,
    resetFormData,
    success,
  };
};
